import { FREQUENCY_VALUES } from '@/app/schedule/_utils/constants'
import { FrequencyValue, ScheduleResponse } from '@/app/schedule/_utils/types'
import { taskManagerApiPaths } from '@/hooks/api/_paths'
import { useApiRequest } from '@/lib/http-utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

const CREATE_SCHEDULE_ERROR = 'FAILED_TO_CREATE_SCHEDULE'

type CreateScheduleAPIRequest = {
  start_time: string
  end_time?: string
  frequency: number
  agent_id: string
  status: string
  tool_args?: Record<string, string>
}

type CreateScheduleAPIResponse = ScheduleResponse

type CreateScheduleParams = {
  agentId: string
  startDate: Date
  endDate?: Date
  startTime: string
  frequency: FrequencyValue
  parameters?: Record<string, string>
}

function useCreateScheduleMutation() {
  const queryClient = useQueryClient()
  const { apiRequest, domainId } = useApiRequest()

  return useMutation({
    mutationKey: ['create-schedule'],
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['schedule-tasks', domainId],
      })
    },
    mutationFn: async (params: CreateScheduleParams): Promise<CreateScheduleAPIResponse> => {
      const requestBody = transformToAPIRequest(params)
      const response = await apiRequest({
        path: taskManagerApiPaths.schedules({ domainId }),
        options: {
          method: 'POST',
          body: JSON.stringify(requestBody),
        },
      })

      if (!response.ok) {
        throw new Error(CREATE_SCHEDULE_ERROR)
      }

      const data = (await response.json()) as CreateScheduleAPIResponse
      return data
    },
  })
}

const transformToAPIRequest = (params: CreateScheduleParams): CreateScheduleAPIRequest => {
  const { agentId, startDate, endDate, startTime, frequency, parameters } = params || {}

  const [hours, minutes] = startTime.split(':').map(Number)
  const startDateTime = dayjs(startDate)
    .hour(hours)
    .minute(minutes)
    .second(0)
    .millisecond(0)
    .toISOString()

  const endDateTime = endDate ? endDate.toISOString() : undefined

  const frequencyInSeconds = getFrequencyInSeconds(frequency)

  return {
    start_time: startDateTime,
    end_time: endDateTime,
    frequency: frequencyInSeconds,
    agent_id: agentId,
    status: 'active',
    tool_args: parameters,
  }
}

export { CREATE_SCHEDULE_ERROR, useCreateScheduleMutation }

export const getFrequencyInSeconds = (frequency: FrequencyValue): number => {
  const frequencyMap: Record<FrequencyValue, number> = {
    [FREQUENCY_VALUES.HOURLY]: 3600,
    [FREQUENCY_VALUES.DAILY]: 86400,
    [FREQUENCY_VALUES.WEEKLY]: 604800,
    [FREQUENCY_VALUES.MONTHLY]: 2592000,
  }

  return frequencyMap[frequency]
}

export const getFrequencyFromSeconds = (seconds: number): FrequencyValue => {
  const reverseMap: Record<number, FrequencyValue> = {
    3600: FREQUENCY_VALUES.HOURLY,
    86400: FREQUENCY_VALUES.DAILY,
    604800: FREQUENCY_VALUES.WEEKLY,
    2592000: FREQUENCY_VALUES.MONTHLY,
  }

  return reverseMap[seconds] || FREQUENCY_VALUES.DAILY
}
